import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { AiFillStar } from 'react-icons/ai';
import { FaShuttleVan } from 'react-icons/fa';
import { PackageGroupCardProps } from '../types/bot.types';
import { formatBotPrice } from './formatBotPrice';
import { formatPackageFlightDateTime } from './packageFlightUtils';

/**
 * One hotel, shown ONCE, with its available package combinations underneath.
 *
 * A package result row is a (deal × flight pair) combination, so the same hotel
 * legitimately comes back several times with different flights, board or
 * nights. The backend groups those rows (`group_id` / `is_group_first` on each
 * summary) and this card renders the hotel identity — image, name, stars,
 * dates, "from" price — a single time. The combination rows themselves are
 * passed in as children so the list keeps ownership of the per-row
 * selecting/picked states.
 *
 * Only what every combination shares is shown here; anything that varies
 * (flights, board, nights, transfer) lives on the rows, so the header can never
 * misstate an option.
 */
const PackageGroupCard: React.FC<PackageGroupCardProps> = ({
  hotelName,
  hotelStars,
  imageUrl,
  nights,
  mealPlan,
  transferIncluded,
  features,
  departure,
  returnDate,
  fromPrice,
  currency,
  optionCount,
  moreCount,
  children,
}) => {
  const t = useSelector((s: RootState) => (s.config.translations as any)?.bot);
  const generalT = useSelector(
    (s: RootState) => (s.config.translations as any)?.general ?? {}
  );

  const depDisplay =
    formatPackageFlightDateTime(departure)?.slice(0, 5) ??
    (typeof departure === 'string' ? departure : '');
  const retDisplay =
    formatPackageFlightDateTime(returnDate)?.slice(0, 5) ??
    (typeof returnDate === 'string' ? returnDate : '');

  const optionsLabel =
    optionCount === 1
      ? t?.result?.flightOptionsOne ?? '1 flight option'
      : (t?.result?.flightOptionsOther ?? '{{count}} flight options').replace(
          '{{count}}',
          String(optionCount)
        );

  const moreLabel =
    moreCount === 1
      ? t?.result?.moreFlightOptionsOne ?? '+1 more flight option for this hotel'
      : (
          t?.result?.moreFlightOptionsOther ??
          '+{{count}} more flight options for this hotel'
        ).replace('{{count}}', String(moreCount));

  return (
    <div className="bot-package-group">
      <div className="bot-package-group__header">
        {imageUrl && (
          <div className="bot-package-group__image-wrap">
            <img
              src={imageUrl}
              alt={hotelName}
              className="bot-package-group__image"
            />
          </div>
        )}
        <div className="bot-package-group__headline">
          <div className="bot-package-group__name">{hotelName}</div>
          {hotelStars > 0 && (
            <div className="bot-package-group__stars">
              {Array.from({ length: Math.min(hotelStars, 5) }).map((_, i) => (
                <AiFillStar key={i} color="#f5a623" />
              ))}
            </div>
          )}
          <div className="bot-package-group__meta">
            {depDisplay && (
              <span>
                📅 {depDisplay}
                {retDisplay ? ` → ${retDisplay}` : ''}
              </span>
            )}
            {/* Nights are only passed when every combination shares them. */}
            {nights !== undefined && (
              <span>
                🌙 {nights} {t?.result?.nights ?? 'nights'}
              </span>
            )}
            <span className="bot-package-group__count">{optionsLabel}</span>
          </div>
          {/* Board and transfer show here only when every combination agrees on
              them — otherwise they live on the rows that differ. */}
          {(mealPlan || transferIncluded || features?.length) && (
            <div className="bot-package-group__badges">
              {/* Supplier feature tags (KOSHER, SKI, SPA…). Not translated: they
                  are supplier values, like a hotel name or a board code — and
                  they are the whole reason a user who asked for "kosher
                  packages" can see WHICH results are kosher. */}
              {features?.map((feature) => (
                <span
                  key={feature}
                  className="bot-result-card__badge bot-result-card__badge--feature"
                >
                  {feature}
                </span>
              ))}
              {mealPlan && (
                <span className="bot-result-card__badge">{mealPlan}</span>
              )}
              {transferIncluded && (
                <span className="bot-result-card__badge bot-result-card__badge--green">
                  <FaShuttleVan />{' '}
                  {t?.result?.transferIncluded ?? 'Transfer included'}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="bot-package-group__price">
          {/* ALP min_price is a per-person "from" price — mirror PackageResultCard. */}
          <span className="bot-package-group__price-prefix">
            {generalT.from ?? 'from'}{' '}
          </span>
          {currency} {formatBotPrice(fromPrice)}
          <span className="bot-package-group__price-suffix">
            {' '}
            {generalT.perPerson ?? '/person'}
          </span>
        </div>
      </div>

      <div className="bot-package-group__options">{children}</div>

      {moreCount > 0 && (
        <div className="bot-package-group__more">{moreLabel}</div>
      )}
    </div>
  );
};

export default PackageGroupCard;
